/* eslint-disable react-hooks/set-state-in-effect, react/no-unescaped-entities --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
import { useEffect, useMemo, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { recordThirdPartyView } from "@/lib/thirdPartyView";
import { useAuth } from "@/context/AuthContext";
import { useChildScope } from "@/context/ChildScopeContext";
import { ChildScopeBadge } from "@/components/ChildScopeBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { VoiceDictateButton } from "@/components/journal/VoiceDictateButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileText, FolderLock, ExternalLink, Lock, Eye, Folder, Shield, Plus, Trash2, AlertTriangle, X, Users } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { ChildPicker, validateChildSelection } from "@/components/ChildPicker";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";

const PRESET_CATS = ["medical", "school", "financial", "general"];
const NEW_FOLDER = "__new__";

const ORDER_TYPES = [
  { v: "safety_order",          l: "Safety Order" },
  { v: "barring_order",         l: "Barring Order" },
  { v: "interim_barring_order", l: "Interim Barring Order" },
  { v: "protection_order",      l: "Protection Order" },
  { v: "custody_order",         l: "Custody Order" },
  { v: "access_order",          l: "Access Order" },
  { v: "other",                 l: "Other" },
];
const ORDER_LABEL = Object.fromEntries(ORDER_TYPES.map((o) => [o.v, o.l]));

const orderTitle = (d) => {
  if (d.order_type === "other") return (d.order_type_other || "").trim() || "Other";
  return ORDER_LABEL[d.order_type] || "Legal Document";
};

const emptyLegalForm = () => ({
  title: "",
  order_type: "safety_order",
  order_type_other: "",
  court_name: "",
  order_reference: "",
  date_issued: "",
  expiry_date: "",
  notes: "",
  is_shared: false,
  files: [],  // File[] — multi-file
});

export default function Documents() {
  const { user, isMediator } = useAuth();
  const { selectedChildId, children: scopeChildren, setSelectedChildId } = useChildScope();
  // v77.49 — Audit trail for third-party viewers.
  useEffect(() => {
    recordThirdPartyView({ resource_type: "documents", resource_label: "Opened documents page" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [docs, setDocs] = useState([]);

  // ---- General doc upload state ----
  const [openGeneral, setOpenGeneral] = useState(false);
  const [genUploading, setGenUploading] = useState(false);
  const [genForm, setGenForm] = useState({ title: "", category: "general", is_shared: false });
  const [genChildIds, setGenChildIds] = useState([]);
  // v77.99.14 — Primary-parent-only toggle: "applies to every child".
  // When ticked, child_ids is ignored and the backend stores the doc
  // with empty child_ids + applies_to_all_children=true. Restricted
  // co-parents never see this toggle (and the backend rejects them
  // 403 if they manage to call it via the API).
  const [genHouseholdWide, setGenHouseholdWide] = useState(false);
  const [genFile, setGenFile] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [folderFilter, setFolderFilter] = useState("all");

  // ---- Legal doc state ----
  const [openLegal, setOpenLegal] = useState(false);
  const [legalUploading, setLegalUploading] = useState(false);
  const [legalForm, setLegalForm] = useState(emptyLegalForm());
  const [legalChildIds, setLegalChildIds] = useState([]);
  const [legalHouseholdWide, setLegalHouseholdWide] = useState(false);
  const [detail, setDetail] = useState(null);  // legal doc to view
  const [confirmDelete, setConfirmDelete] = useState(null); // legal doc to delete

  const load = async () => {
    const url = selectedChildId ? `/documents?child_id=${encodeURIComponent(selectedChildId)}` : "/documents";
    const { data } = await api.get(url);
    setDocs(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedChildId]);

  const legalDocs = useMemo(() => docs.filter((d) => d.doc_type === "legal"), [docs]);
  const generalDocs = useMemo(() => docs.filter((d) => (d.doc_type || "general") !== "legal"), [docs]);

  const allFolders = useMemo(() => {
    const fromDocs = Array.from(new Set(generalDocs.map((d) => d.category).filter(Boolean)));
    return Array.from(new Set([...PRESET_CATS, ...fromDocs]));
  }, [generalDocs]);

  const selectItems = useMemo(() => {
    const items = allFolders.map((c) => ({ value: c, label: c }));
    items.push({ value: NEW_FOLDER, label: "+ New folder…" });
    return items;
  }, [allFolders]);

  // ============ General Documents ============
  const submitGeneral = async (e) => {
    e.preventDefault();
    if (!genFile) { toast.error("Select a file"); return; }
    const validationErr = genHouseholdWide
      ? null  // household-wide intent — backend bypasses per-child requirement
      : validateChildSelection({
          value: genChildIds,
          hasChildren: (scopeChildren || []).length > 0,
          selectedChildId,
        });
    if (validationErr) { toast.error(validationErr); return; }
    let category = genForm.category;
    if (showNewFolder) {
      const name = newFolderName.trim().toLowerCase();
      if (!name) { toast.error("Enter a folder name"); return; }
      category = name;
    }
    setGenUploading(true);
    try {
      const fd = new FormData(); fd.append("file", genFile);
      const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/documents", {
        title: genForm.title,
        category,
        doc_type: "general",
        is_shared: genForm.is_shared,
        child_ids: genHouseholdWide ? [] : genChildIds,
        applies_to_all_children: genHouseholdWide,
        file_id: up.data.id,
      });
      toast.success("Document uploaded");
      setOpenGeneral(false);
      setGenForm({ title: "", category: "general", is_shared: false });
      setGenChildIds(selectedChildId ? [selectedChildId] : []);
      setGenHouseholdWide(false);
      setGenFile(null);
      setNewFolderName("");
      setShowNewFolder(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setGenUploading(false);
    }
  };

  // ============ Legal Documents ============
  const submitLegal = async (e) => {
    e.preventDefault();
    if (legalForm.files.length === 0) { toast.error("Attach at least one file"); return; }
    if (!legalForm.date_issued) { toast.error("Date issued is required"); return; }
    if (legalForm.order_type === "other" && !legalForm.order_type_other.trim()) {
      toast.error("Describe the order type");
      return;
    }
    const validationErr = legalHouseholdWide
      ? null
      : validateChildSelection({
          value: legalChildIds,
          hasChildren: (scopeChildren || []).length > 0,
          selectedChildId,
        });
    if (validationErr) { toast.error(validationErr); return; }
    setLegalUploading(true);
    try {
      // Upload all files in parallel — keep it simple, not a chunked queue.
      const uploads = await Promise.all(legalForm.files.map(async (f) => {
        const fd = new FormData(); fd.append("file", f);
        const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return up.data.id;
      }));
      const titleFallback = legalForm.title.trim() || (legalForm.order_type === "other"
        ? (legalForm.order_type_other.trim() || "Legal Document")
        : ORDER_LABEL[legalForm.order_type]);
      await api.post("/documents", {
        title: titleFallback,
        category: "legal",
        doc_type: "legal",
        is_shared: legalForm.is_shared,
        child_ids: legalHouseholdWide ? [] : legalChildIds,
        applies_to_all_children: legalHouseholdWide,
        order_type: legalForm.order_type,
        order_type_other: legalForm.order_type === "other" ? legalForm.order_type_other.trim() : null,
        court_name: legalForm.court_name.trim() || null,
        order_reference: legalForm.order_reference.trim() || null,
        date_issued: legalForm.date_issued,
        expiry_date: legalForm.expiry_date || null,
        notes: legalForm.notes.trim() || null,
        file_ids: uploads,
      });
      toast.success("Legal document saved");
      setOpenLegal(false);
      setLegalForm(emptyLegalForm());
      setLegalChildIds(selectedChildId ? [selectedChildId] : []);
      setLegalHouseholdWide(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setLegalUploading(false);
    }
  };

  const deleteDoc = async (doc) => {
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Legal document deleted");
      setConfirmDelete(null);
      setDetail(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  // ============ Common ============
  const openFile = (file_id) => {
    window.open(`${API}/files/${file_id}/download`, "_blank");
  };
  const toggleShare = async (d) => {
    try {
      await api.patch(`/documents/${d.id}/share`, { is_shared: !d.is_shared });
      toast.success(!d.is_shared ? "Shared with co-parent" : "Marked private");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const onSelectCategory = (v) => {
    if (v === NEW_FOLDER) {
      setShowNewFolder(true);
      setGenForm({ ...genForm, category: "general" });
    } else {
      setShowNewFolder(false);
      setGenForm({ ...genForm, category: v });
    }
  };

  // v77.99.14 — In "All children" master mode we no longer block the
  // page. Documents that apply to the whole household (separation
  // agreement, mortgage, family insurance) deserve first-class
  // visibility; per-child documents pick up a coloured pill on each
  // card so the scope is obvious. The legacy per-child wall was
  // friction without privacy benefit — the data model already
  // supports `child_ids: []` (= household-wide).
  const isMasterMode = !selectedChildId && (scopeChildren || []).length > 0;
  // ChildId → child object lookup for rendering coloured pills on
  // each row. Stable for the lifetime of the page render.
  const childById = useMemo(() => {
    const m = new Map();
    (scopeChildren || []).forEach((c) => m.set(c.id, c));
    return m;
  }, [scopeChildren]);
  // v77.99.14 — Scope filter chip: "all" | "household" | "per_child".
  // "Household-wide" = `child_ids` is empty/missing (intent is "applies
  // to every child"). "Per-child" = at least one child_id present.
  const [scopeFilter, setScopeFilter] = useState("all");
  const scopeOf = (d) => ((d.child_ids || []).length === 0 ? "household" : "per_child");

  // v77.99.14 — Apply BOTH the folder filter and the new scope filter
  // (all / household-wide / per-child). Two narrow filters compose
  // cleanly so a user can ask "show me household-wide medical docs"
  // in one step.
  const filteredGeneral = generalDocs
    .filter((d) => folderFilter === "all" || d.category === folderFilter)
    .filter((d) => scopeFilter === "all" || scopeOf(d) === scopeFilter);
  const groupedGeneral = filteredGeneral.reduce((acc, d) => {
    (acc[d.category || "general"] = acc[d.category || "general"] || []).push(d);
    return acc;
  }, {});

  const fileList = legalForm.files;
  const removeFile = (idx) => setLegalForm({ ...legalForm, files: fileList.filter((_, i) => i !== idx) });
  const addFiles = (newOnes) => setLegalForm({ ...legalForm, files: [...fileList, ...newOnes] });

  return (
    <div className="space-y-8" data-testid="documents-page">
      <div>
        <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">Vault</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium mt-1 inline-flex items-center gap-3 flex-wrap">Documents<ChildScopeBadge testid="documents-child-scope-badge" /></h1>
        <p className="text-sm text-[#6F828A] mt-1">Organised in folders. Private by default — share only what's needed.</p>
      </div>

      {/* v77.99.14 — Master-mode hint chip. Replaces the old "Please
          pick a child" wall. Lets parents jump straight into a
          per-child view if they want, or stay in master mode and
          see the whole household. */}
      {isMasterMode && (
        <div
          className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 border border-[#D9E1E3] bg-[#F4FAFA] rounded-md p-3 sm:p-4"
          data-testid="documents-master-mode-banner"
        >
          <div className="flex items-start sm:items-center gap-2.5 flex-1 min-w-0">
            <Users className="w-4 h-4 text-[#1F9FA4] flex-shrink-0 mt-0.5 sm:mt-0" strokeWidth={1.75} />
            <p className="text-sm text-[#2E3A3F] leading-relaxed">
              Showing all documents across your household. Each card shows which children it applies to.
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm border-[#D9E1E3] text-[#2E3A3F] flex-shrink-0"
                data-testid="documents-choose-child-button"
              >
                <Users className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} /> Filter to one child
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={6}
              className="w-60 bg-white border-[#D9E1E3]"
              data-testid="documents-choose-child-menu"
            >
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] font-medium">
                Pick a child
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#D9E1E3]" />
              {(scopeChildren || []).map((c) => (
                <DropdownMenuItem
                  key={c.id}
                  onSelect={() => setSelectedChildId(c.id)}
                  className="cursor-pointer focus:bg-[#EEF3F4]"
                  data-testid={`documents-pick-child-${c.id}`}
                >
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full mr-2.5 flex-shrink-0"
                    style={{ backgroundColor: c.color || "#1F9FA4" }}
                    aria-hidden="true"
                  />
                  <span className="flex-1 text-sm text-[#2E3A3F]">{c.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* ============= LEGAL DOCUMENTS SECTION ============= */}
      <section
        className="rounded-md border-2 border-[#A7C5DA] bg-gradient-to-br from-[#F2F6FA] to-[#EBF1F7] p-4 sm:p-5 space-y-3 sm:space-y-4"
        data-testid="legal-documents-section"
      >
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-sm bg-white border border-[#A7C5DA] text-[#2E4A66] inline-flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5" strokeWidth={1.6} />
          </span>
          <h2 className="font-heading text-lg sm:text-xl font-medium text-[#2E3A3F]">Legal Documents</h2>
        </div>
        <p className="text-sm text-[#6F828A] leading-relaxed">
          Court orders and sensitive legal records. Private by default. Share only if needed.
        </p>
        <div className="rounded-sm border border-[#E0CBA4] bg-[#FBF5E8] px-3 py-2.5 flex gap-2.5" data-testid="legal-warning-banner">
          <AlertTriangle className="w-4 h-4 text-[#8C6A2A] mt-0.5 flex-shrink-0" strokeWidth={1.8} />
          <ul className="text-xs sm:text-sm text-[#5C4719] space-y-1 leading-relaxed">
            <li>Only upload <strong>copies</strong> of legal documents. Do not upload originals or irreplaceable files.</li>
            <li>Documents are <strong>private by default</strong>. Only share if necessary.</li>
          </ul>
        </div>
        {!isMediator && (
          <Dialog
            open={openLegal}
            onOpenChange={(v) => {
              setOpenLegal(v);
              if (v) setLegalChildIds(selectedChildId ? [selectedChildId] : []);
              if (!v) {
                setLegalForm(emptyLegalForm());
                setLegalChildIds(selectedChildId ? [selectedChildId] : []);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button className="bg-[#2E4A66] hover:bg-[#22384F] rounded-sm w-full sm:w-auto min-h-[44px] font-medium" data-testid="add-legal-document-button">
                <Plus className="w-4 h-4 mr-2" /> Add Legal Document
              </Button>
            </DialogTrigger>
            <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading inline-flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#2E4A66]" strokeWidth={1.6} /> Add Legal Document
                </DialogTitle>
              </DialogHeader>
              <UpgradePrompt feature="add legal documents" testid="legal-upgrade-prompt" />
              <div className="rounded-sm border border-[#E0CBA4] bg-[#FBF5E8] px-3 py-2.5 flex gap-2.5 mb-1" data-testid="legal-warning-banner-dialog">
                <AlertTriangle className="w-4 h-4 text-[#8C6A2A] mt-0.5 flex-shrink-0" strokeWidth={1.8} />
                <ul className="text-xs text-[#5C4719] space-y-1 leading-relaxed">
                  <li>Only upload <strong>copies</strong>. Do not upload originals or irreplaceable files.</li>
                  <li>Documents are <strong>private by default</strong>. Only share if necessary.</li>
                </ul>
              </div>
                <form onSubmit={submitLegal} className="space-y-4">
                  <div>
                    <Label>Order Type <span className="text-[#C0291D]">*</span></Label>
                    <Select value={legalForm.order_type} onValueChange={(v) => setLegalForm({ ...legalForm, order_type: v })}>
                      <SelectTrigger data-testid="legal-order-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ORDER_TYPES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {legalForm.order_type === "other" && (
                      <Input
                        className="mt-2"
                        placeholder="Describe the order type"
                        value={legalForm.order_type_other}
                        onChange={(e) => setLegalForm({ ...legalForm, order_type_other: e.target.value })}
                        data-testid="legal-order-type-other"
                      />
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Court name <span className="text-[#6F828A] font-normal">(optional)</span></Label>
                      <Input value={legalForm.court_name} onChange={(e) => setLegalForm({ ...legalForm, court_name: e.target.value })} data-testid="legal-court-name" />
                    </div>
                    <div>
                      <Label>Order reference number</Label>
                      <Input value={legalForm.order_reference} onChange={(e) => setLegalForm({ ...legalForm, order_reference: e.target.value })} placeholder="e.g. FCA-2024-001234" data-testid="legal-order-reference" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Date issued <span className="text-[#C0291D]">*</span></Label>
                      <Input type="date" value={legalForm.date_issued} onChange={(e) => setLegalForm({ ...legalForm, date_issued: e.target.value })} required data-testid="legal-date-issued" />
                    </div>
                    <div>
                      <Label>Expiry date <span className="text-[#6F828A] font-normal">(optional)</span></Label>
                      <Input type="date" value={legalForm.expiry_date} onChange={(e) => setLegalForm({ ...legalForm, expiry_date: e.target.value })} data-testid="legal-expiry-date" />
                    </div>
                  </div>
                  <div>
                    <Label>Notes <span className="text-[#6F828A] font-normal">(optional)</span></Label>
                    <Textarea rows={3} value={legalForm.notes} onChange={(e) => setLegalForm({ ...legalForm, notes: e.target.value })} data-testid="legal-notes" />
                    {/* v77.99.55 — Voice-to-text drop-in. Same Web
                        Speech component as Journal / Messages /
                        Requests / Expenses dispute. Smart append so
                        typed notes are preserved across recording
                        sessions; never auto-submits. */}
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <VoiceDictateButton
                        testidPrefix="legal-notes-voice"
                        onTranscript={(t) =>
                          setLegalForm((f) => ({ ...f, notes: (f.notes ? f.notes + (f.notes.endsWith(" ") ? "" : " ") : "") + t }))
                        }
                      />
                      <span className="text-[11px] text-[#6F828A] leading-snug">
                        Use voice-to-text to draft your notes. You can review and edit before saving.
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label>Files <span className="text-[#C0291D]">*</span> <span className="text-[#6F828A] font-normal text-xs">PDF, JPG, PNG · multiple allowed</span></Label>
                    <Input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png"
                      multiple
                      onChange={(e) => addFiles(Array.from(e.target.files || []))}
                      data-testid="legal-files-input"
                    />
                    {fileList.length > 0 && (
                      <ul className="mt-2 space-y-1.5" data-testid="legal-files-list">
                        {fileList.map((f, idx) => (
                          <li key={`${f.name}-${f.size}-${f.lastModified}-${idx}`} className="flex items-center gap-2 px-2.5 py-1.5 border border-[#D9E1E3] bg-[#F5F7F8] rounded-sm text-xs" data-testid={`legal-files-item-${idx}`}>
                            <FileText className="w-3.5 h-3.5 text-[#6F828A]" strokeWidth={1.5} />
                            <span className="flex-1 min-w-0 truncate">{f.name}</span>
                            <span className="text-[#6F828A] tabular-nums">{(f.size / 1024).toFixed(1)} KB</span>
                            <button
                              type="button"
                              onClick={() => removeFile(idx)}
                              className="text-[#6F828A] hover:text-[#C0291D] inline-flex items-center"
                              aria-label="Remove file"
                              data-testid={`legal-files-remove-${idx}`}
                            >
                              <X className="w-3.5 h-3.5" strokeWidth={1.8} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {/* v77.99.14 — Primary-parent-only household-wide
                      toggle. When ON, the ChildPicker collapses to
                      an info row and the doc is saved with empty
                      child_ids + applies_to_all_children=true. */}
                  {user?.is_primary_parent && (scopeChildren || []).length > 1 && (
                    <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="legal-applies-all-toggle">
                      <input
                        type="checkbox"
                        checked={legalHouseholdWide}
                        onChange={(e) => setLegalHouseholdWide(e.target.checked)}
                        className="mt-0.5 accent-[#1F9FA4]"
                        data-testid="legal-applies-all-checkbox"
                      />
                      <span>
                        <span className="font-medium text-[#2E3A3F]">This document applies to every child in our household</span>
                        <span className="block text-xs text-[#6F828A] mt-0.5">e.g. separation agreement, family insurance, mortgage. You won't need to tick each child.</span>
                      </span>
                    </label>
                  )}
                  {!legalHouseholdWide && (
                    <ChildPicker
                      value={legalChildIds}
                      onChange={setLegalChildIds}
                      testPrefix="legal-child-picker"
                      label="Which child does this legal document relate to?"
                      hint="Picking a child means only co-parents linked to that child can see this document."
                    />
                  )}
                  <div className="flex items-center justify-between border border-[#D9E1E3] rounded-sm p-3">
                    <div>
                      <div className="text-sm font-medium inline-flex items-center gap-1.5">
                        <Lock className="w-3.5 h-3.5 text-[#6F828A]" strokeWidth={1.6} /> Visible only to you
                      </div>
                      <div className="text-xs text-[#6F828A] mt-0.5">Toggle on to also share with your co-parent.</div>
                    </div>
                    <Switch
                      checked={legalForm.is_shared}
                      onCheckedChange={(v) => setLegalForm({ ...legalForm, is_shared: v })}
                      data-testid="legal-share-switch"
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={legalUploading}
                      className="bg-[#2E4A66] hover:bg-[#22384F]"
                      data-testid="legal-save-button"
                    >
                      {legalUploading ? "Saving…" : "Save legal document"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
        )}

        {legalDocs.length === 0 ? (
          <div className="rounded-sm bg-white/70 border border-dashed border-[#A7C5DA] py-6 px-4 text-center" data-testid="legal-empty">
            <Shield className="w-9 h-9 mx-auto text-[#6F828A]" strokeWidth={1.5} />
            <p className="mt-3 text-sm font-medium text-[#2E3A3F]">No legal documents yet.</p>
            <p className="mt-1 text-sm text-[#6F828A]">Your court documents are private by default.</p>
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="legal-list">
            {legalDocs.map((d) => {
              const fileCount = (d.file_ids || []).length || (d.file_id ? 1 : 0);
              // v77.99.14 — same applies-to scope pills as general docs.
              const docChildren = (d.child_ids || [])
                .map((cid) => childById.get(cid))
                .filter(Boolean);
              const isHouseholdWide = (d.child_ids || []).length === 0;
              return (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => setDetail(d)}
                    className="w-full text-left bg-white border border-[#A7C5DA] hover:bg-[#F2F6FA] rounded-md p-4 transition-colors"
                    data-testid={`legal-card-${d.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="w-8 h-8 rounded-sm bg-[#EBF1F7] text-[#2E4A66] inline-flex items-center justify-center flex-shrink-0">
                        <Shield className="w-4 h-4" strokeWidth={1.6} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#2E3A3F] truncate">{orderTitle(d)}</div>
                        {d.order_reference && (
                          <div className="text-xs text-[#6F828A] mt-0.5 font-mono-data truncate" data-testid={`legal-card-ref-${d.id}`}>
                            Ref · {d.order_reference}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-[0.1em] border border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A] px-1.5 py-0.5 rounded-sm font-mono-data">
                            {d.date_issued ? formatDate(d.date_issued) : "—"}
                          </span>
                          <span className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border rounded-sm inline-flex items-center gap-1 ${d.is_shared ? "border-[#4A6170] bg-[#E6ECEF] text-[#2E3E48]" : "border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A]"}`}>
                            {d.is_shared ? <Eye className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                            {d.is_shared ? "Shared" : "Private"}
                          </span>
                          {fileCount > 1 && (
                            <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border border-[#A7C5DA] bg-white text-[#2E4A66] rounded-sm tabular-nums">{fileCount} files</span>
                          )}
                        </div>
                        {isMasterMode && (
                          <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid={`legal-applies-to-${d.id}`}>
                            {isHouseholdWide ? (
                              <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border rounded-sm inline-flex items-center gap-1 border-[#A7D8DA] bg-[#E0F2F2] text-[#0B4D50]">
                                <Users className="w-3 h-3" strokeWidth={2} /> All children
                              </span>
                            ) : (
                              docChildren.map((c) => (
                                <span
                                  key={c.id}
                                  className="text-[10px] px-1.5 py-0.5 border rounded-sm inline-flex items-center gap-1 border-[#D9E1E3] bg-white text-[#2E3A3F]"
                                >
                                  <span
                                    className="inline-block w-2 h-2 rounded-full"
                                    style={{ backgroundColor: c.color || "#1F9FA4" }}
                                    aria-hidden="true"
                                  />
                                  {c.name}
                                </span>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ============= GENERAL DOCUMENTS SECTION ============= */}
      <section className="space-y-3 sm:space-y-4" data-testid="general-documents-section">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-sm bg-[#EEF3F4] border border-[#D9E1E3] text-[#6F828A] inline-flex items-center justify-center flex-shrink-0">
            <Folder className="w-5 h-5" strokeWidth={1.6} />
          </span>
          <h2 className="font-heading text-lg sm:text-xl font-medium text-[#2E3A3F]">General Documents</h2>
        </div>
        <p className="text-sm text-[#6F828A] leading-relaxed">
          Medical records, school forms, financial paperwork, and other shared files.
        </p>
        {/* v77.99.14 — Scope filter chips (master mode only). Lets
            parents jump between "house-wide" and "per-child" docs in
            one click without losing the selected folder. Hidden in
            single-child mode because every doc is by definition for
            that child. */}
        {isMasterMode && (
          <div
            role="tablist"
            aria-label="Filter by who the document applies to"
            className="inline-flex flex-wrap gap-2"
            data-testid="doc-scope-filter"
          >
            {[
              { value: "all", label: "All", testid: "doc-scope-all" },
              { value: "household", label: "Household-wide", testid: "doc-scope-household" },
              { value: "per_child", label: "Per child", testid: "doc-scope-per-child" },
            ].map((opt) => {
              const active = scopeFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setScopeFilter(opt.value)}
                  className={`text-xs px-3 h-8 rounded-sm border transition-colors ${active
                    ? "bg-[#116B6E] border-[#116B6E] text-white"
                    : "bg-white border-[#D9E1E3] text-[#6F828A] hover:bg-[#EEF3F4]"}`}
                  data-testid={opt.testid}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
          <Select value={folderFilter} onValueChange={setFolderFilter}>
            <SelectTrigger className="w-full sm:w-[170px] min-h-[44px]" data-testid="doc-folder-filter">
              <SelectValue placeholder="All folders" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All folders</SelectItem>
              {allFolders.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {!isMediator && (
            <Dialog
              open={openGeneral}
              onOpenChange={(v) => {
                setOpenGeneral(v);
                if (v) setGenChildIds(selectedChildId ? [selectedChildId] : []);
                if (!v) {
                  setShowNewFolder(false);
                  setNewFolderName("");
                  setGenChildIds(selectedChildId ? [selectedChildId] : []);
                }
              }}
            >
              <DialogTrigger asChild>
                <Button className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm w-full sm:w-auto min-h-[44px] font-medium" data-testid="upload-document-button">
                  <Upload className="w-4 h-4 mr-2" /> Upload General Document
                </Button>
              </DialogTrigger>
              <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3]">
                <DialogHeader><DialogTitle className="font-heading">Upload general document</DialogTitle></DialogHeader>
                <UpgradePrompt feature="upload documents" testid="documents-upgrade-prompt" />
                <form onSubmit={submitGeneral} className="space-y-3">
                  <div><Label>Title</Label><Input value={genForm.title} onChange={(e) => setGenForm({ ...genForm, title: e.target.value })} required data-testid="doc-title-input" /></div>
                  <div>
                    <Label>Folder</Label>
                    <Select value={showNewFolder ? NEW_FOLDER : genForm.category} onValueChange={onSelectCategory}>
                      <SelectTrigger data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{selectItems.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                    {showNewFolder && (
                      <Input className="mt-2" placeholder="Folder name (e.g. activities)" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} data-testid="doc-new-folder-input" />
                    )}
                  </div>
                  <div><Label>File</Label><Input type="file" onChange={(e) => setGenFile(e.target.files?.[0] || null)} required data-testid="doc-file-input" /></div>
                  {user?.is_primary_parent && (scopeChildren || []).length > 1 && (
                    <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="doc-applies-all-toggle">
                      <input
                        type="checkbox"
                        checked={genHouseholdWide}
                        onChange={(e) => setGenHouseholdWide(e.target.checked)}
                        className="mt-0.5 accent-[#1F9FA4]"
                        data-testid="doc-applies-all-checkbox"
                      />
                      <span>
                        <span className="font-medium text-[#2E3A3F]">This document applies to every child in our household</span>
                        <span className="block text-xs text-[#6F828A] mt-0.5">e.g. separation agreement, family insurance, mortgage. You won't need to tick each child.</span>
                      </span>
                    </label>
                  )}
                  {!genHouseholdWide && (
                    <ChildPicker
                      value={genChildIds}
                      onChange={setGenChildIds}
                      testPrefix="doc-child-picker"
                      label="Which child does this document belong to?"
                      hint="Picking a child means only co-parents linked to that child can see this document."
                    />
                  )}
                  <div className="flex items-center justify-between border border-[#D9E1E3] rounded-sm p-3">
                    <div>
                      <div className="text-sm font-medium">Share with co-parent</div>
                      <div className="text-xs text-[#6F828A]">Off keeps this document private to you.</div>
                    </div>
                    <Switch checked={genForm.is_shared} onCheckedChange={(v) => setGenForm({ ...genForm, is_shared: v })} data-testid="doc-share-switch" />
                  </div>
                  <DialogFooter><Button type="submit" disabled={genUploading} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="doc-save-button">{genUploading ? "Uploading…" : "Upload"}</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {filteredGeneral.length === 0 ? (
          <div className="border border-[#D9E1E3] bg-white rounded-md py-8 px-4 text-center" data-testid="general-empty">
            <FolderLock className="w-9 h-9 mx-auto text-[#6F828A]" strokeWidth={1.5} />
            <p className="mt-3 text-sm font-medium text-[#2E3A3F]">{folderFilter === "all" ? "No general documents yet." : "No documents in this folder yet."}</p>
            {folderFilter === "all" && (
              <p className="mt-1 text-sm text-[#6F828A]">Upload school forms, medical records or anything else you'd like to keep organised.</p>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedGeneral).map(([cat, items]) => (
              <div key={cat} data-testid={`folder-${cat}`}>
                <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A] mb-2 flex items-center gap-1.5">
                  <Folder className="w-3.5 h-3.5" strokeWidth={1.5} /> {cat} <span className="text-[#A5B4BB]">· {items.length}</span>
                </div>
                <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {items.map((d) => {
                    const mine = d.uploaded_by === user?.id;
                    // v77.99.14 — Per-card "applies to" pills.
                    // `child_ids` empty / missing means it's a
                    // household-wide doc (separation agreement,
                    // mortgage, family insurance). Only render the
                    // pills in master mode — in a per-child view the
                    // scope is implicit.
                    const docChildren = (d.child_ids || [])
                      .map((cid) => childById.get(cid))
                      .filter(Boolean);
                    const isHouseholdWide = (d.child_ids || []).length === 0;
                    return (
                      <li key={d.id} className="border border-[#D9E1E3] bg-white rounded-md p-4 hover:bg-[#EEF3F4] transition-colors" data-testid={`document-${d.id}`}>
                        <div className="flex items-start gap-3">
                          <FileText className="w-5 h-5 text-[#6F828A] mt-0.5" strokeWidth={1.5} />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{d.title}</div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border rounded-sm inline-flex items-center gap-1 ${d.is_shared ? "border-[#4A6170] bg-[#E6ECEF] text-[#2E3E48]" : "border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A]"}`} data-testid={`doc-visibility-${d.id}`}>
                                {d.is_shared ? <Eye className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                                {d.is_shared ? "Shared" : "Private"}
                              </span>
                              <span className="text-xs text-[#6F828A] font-mono-data">{formatDate(d.created_at)}</span>
                            </div>
                            {/* Applies-to pills: household-wide marker
                                OR coloured chips per linked child. */}
                            {isMasterMode && (
                              <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid={`doc-applies-to-${d.id}`}>
                                {isHouseholdWide ? (
                                  <span
                                    className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border rounded-sm inline-flex items-center gap-1 border-[#A7D8DA] bg-[#E0F2F2] text-[#0B4D50]"
                                    data-testid={`doc-pill-household-${d.id}`}
                                  >
                                    <Users className="w-3 h-3" strokeWidth={2} />
                                    All children
                                  </span>
                                ) : (
                                  docChildren.map((c) => (
                                    <span
                                      key={c.id}
                                      className="text-[10px] px-1.5 py-0.5 border rounded-sm inline-flex items-center gap-1 border-[#D9E1E3] bg-white text-[#2E3A3F]"
                                      data-testid={`doc-pill-child-${d.id}-${c.id}`}
                                    >
                                      <span
                                        className="inline-block w-2 h-2 rounded-full"
                                        style={{ backgroundColor: c.color || "#1F9FA4" }}
                                        aria-hidden="true"
                                      />
                                      {c.name}
                                    </span>
                                  ))
                                )}
                                {/* If a doc has child_ids referencing
                                    children we can't see (rare —
                                    happens when a co-parent has been
                                    removed from a child), show a
                                    quiet count placeholder. */}
                                {!isHouseholdWide && docChildren.length === 0 && (
                                  <span
                                    className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border rounded-sm border-[#D9E1E3] bg-[#F4F8F9] text-[#6F828A]"
                                    data-testid={`doc-pill-restricted-${d.id}`}
                                  >
                                    Restricted scope
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => openFile(d.file_id)} className="hover:bg-[#E8F0F1]" data-testid={`open-document-${d.id}`}>
                            <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                        </div>
                        {mine && !isMediator && (
                          <div className="mt-2 flex items-center justify-end">
                            <button type="button" onClick={() => toggleShare(d)} className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] hover:text-[#2E3A3F]" data-testid={`doc-toggle-share-${d.id}`}>
                              {d.is_shared ? "Make private" : "Share"}
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ============= LEGAL DETAIL DIALOG ============= */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-h-[90vh] overflow-y-auto" data-testid="legal-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading inline-flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#2E4A66]" strokeWidth={1.6} /> {orderTitle(detail)}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <dl className="grid sm:grid-cols-2 gap-3">
                  <Field label="Order type" value={ORDER_LABEL[detail.order_type] || "—"} testid="detail-order-type" />
                  {detail.order_type === "other" && <Field label="Description" value={detail.order_type_other || "—"} testid="detail-order-type-other" />}
                  <Field label="Court" value={detail.court_name || "—"} testid="detail-court" />
                  <Field label="Reference number" value={detail.order_reference || "—"} testid="detail-ref" mono />
                  <Field label="Date issued" value={detail.date_issued ? formatDate(detail.date_issued) : "—"} testid="detail-date-issued" mono />
                  <Field label="Expiry date" value={detail.expiry_date ? formatDate(detail.expiry_date) : "—"} testid="detail-expiry" mono />
                </dl>
                {detail.notes && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] mb-1">Notes</div>
                    <div className="border border-[#D9E1E3] bg-[#F5F7F8] rounded-sm p-3 whitespace-pre-wrap" data-testid="detail-notes">{detail.notes}</div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] mb-2">Attached files ({(detail.file_ids || []).length})</div>
                  <ul className="space-y-1.5" data-testid="detail-files">
                    {(detail.file_ids || []).map((fid, i) => (
                      <li key={fid} className="flex items-center gap-2 px-3 py-2 border border-[#D9E1E3] bg-white rounded-sm text-sm" data-testid={`detail-file-${i}`}>
                        <FileText className="w-4 h-4 text-[#6F828A]" strokeWidth={1.5} />
                        <span className="flex-1">File {i + 1}</span>
                        <Button size="sm" variant="outline" onClick={() => openFile(fid)} className="border-[#D9E1E3] rounded-sm" data-testid={`detail-file-open-${i}`}>
                          <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-[#D9E1E3]">
                  <span className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border rounded-sm inline-flex items-center gap-1 ${detail.is_shared ? "border-[#4A6170] bg-[#E6ECEF] text-[#2E3E48]" : "border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A]"}`}>
                    {detail.is_shared ? <Eye className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                    {detail.is_shared ? "Shared with co-parent" : "Visible only to you"}
                  </span>
                  {detail.uploaded_by === user?.id && !isMediator && (
                    <button type="button" onClick={() => toggleShare(detail)} className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] hover:text-[#2E3A3F]" data-testid="detail-toggle-share">
                      {detail.is_shared ? "Make private" : "Share with co-parent"}
                    </button>
                  )}
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {detail.uploaded_by === user?.id && !isMediator && (
                  <Button
                    variant="outline"
                    onClick={() => setConfirmDelete(detail)}
                    className="border-[#C0291D] text-[#C0291D] hover:bg-[#FBEFED] rounded-sm w-full sm:w-auto"
                    data-testid="legal-delete-button"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                  </Button>
                )}
                <Button onClick={() => setDetail(null)} className="bg-[#1F9FA4] hover:bg-[#178085] w-full sm:w-auto" data-testid="legal-close-button">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ============= DELETE CONFIRM DIALOG ============= */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3]" data-testid="legal-confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading inline-flex items-center gap-2 text-[#8C2A22]">
              <AlertTriangle className="w-4 h-4" strokeWidth={1.8} /> Delete legal document?
            </DialogTitle>
          </DialogHeader>
          {confirmDelete && (
            <div className="space-y-3 text-sm">
              <p>You're about to permanently delete <strong>{orderTitle(confirmDelete)}</strong>{confirmDelete.order_reference ? <> (<span className="font-mono-data">{confirmDelete.order_reference}</span>)</> : null}.</p>
              <p className="text-[#6F828A]">All attached files will be removed from secure storage. This cannot be undone.</p>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setConfirmDelete(null)} className="w-full sm:w-auto" data-testid="legal-confirm-delete-cancel">Cancel</Button>
            <Button onClick={() => confirmDelete && deleteDoc(confirmDelete)} className="bg-[#C0291D] hover:bg-[#8C2A22] w-full sm:w-auto" data-testid="legal-confirm-delete-button">
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value, testid, mono }) => (
  <div>
    <dt className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] mb-0.5">{label}</dt>
    <dd className={`text-[#2E3A3F] ${mono ? "font-mono-data" : ""}`} data-testid={testid}>{value}</dd>
  </div>
);
